import { SmartTableColumn, WithNAFallback } from '@toggle/design-system';
import { formatPercentage, getCurrencySymbol } from '@toggle/helpers';
import { EarningsItemBase, FinancialData } from '@toggle/toggle';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { formatNumberWithUnit } from '~/widgets/earnings/utils/utils';

import { SharedTable } from '../../../components/shared-table/SharedTable';
import * as S from './EarningsExpectationsTable.styles';

export interface EarningsExpectationsTableProps {
  earningsData: EarningsItemBase;
}

export const EarningsExpectationsTable = ({
  earningsData,
}: EarningsExpectationsTableProps) => {
  const { t } = useTranslation(['earnings']);
  const { eps, revenue } = earningsData;

  const naContainerPadding = '10px 12px';

  const tableData = [
    {
      name: t('earnings:earningsInsight.drawer.earningsPreview.tables.eps'),
      key: 'eps',
      ...eps,
    },
    {
      name: t('earnings:earningsInsight.drawer.earningsPreview.tables.revenue'),
      key: 'revenue',
      ...revenue,
    },
  ];

  const tableColumns: SmartTableColumn<
    FinancialData & { name: string; key: string }
  >[] = [
    {
      label: 'Name',
      key: 'name',
      renderHeader: () => null,
      render: value => <S.StyledNameCell>{value}</S.StyledNameCell>,
    },
    {
      label: t(
        'earnings:earningsInsight.drawer.earningsPreview.tables.estimate'
      ),
      key: 'estimate',
      alignment: 'right',
      render: value => {
        const currencySymbol = getCurrencySymbol(earningsData.currency);
        return (
          <WithNAFallback
            containerPadding={naContainerPadding}
            check={value !== undefined && value !== null}
            value={
              <S.NumberCell>{`${currencySymbol}${formatNumberWithUnit(
                value
              )}`}</S.NumberCell>
            }
          />
        );
      },
    },
    {
      label: t('earnings:earningsInsight.drawer.earningsPreview.tables.yoy'),
      key: 'estimate_yoy_percentage',
      alignment: 'right',
      render: value => (
        <WithNAFallback
          containerPadding={naContainerPadding}
          value={
            value !== undefined && (
              <S.YoyCell $isPositive={value > 0} $isZero={value === 0}>
                {formatPercentage(value, {
                  suffix: '%',
                  addPlus: true,
                  useOriginalValue: true,
                })}
              </S.YoyCell>
            )
          }
        />
      ),
    },
  ];

  return (
    <SharedTable
      title={t(
        'earnings:earningsInsight.drawer.earningsPreview.earningsExpectations'
      )}
      tableData={tableData}
      tableColumns={tableColumns}
      noCellPadding
    />
  );
};
